import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ThermostatRoundedIcon from "@mui/icons-material/ThermostatRounded";
import MonitorHeartRoundedIcon from "@mui/icons-material/MonitorHeartRounded";
import PsychologyRoundedIcon from "@mui/icons-material/PsychologyRounded";
import AccessibilityNewRoundedIcon from "@mui/icons-material/AccessibilityNewRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import MedicalServicesRoundedIcon from "@mui/icons-material/MedicalServicesRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import ChildCareRoundedIcon from "@mui/icons-material/ChildCareRounded";
import MedicationRoundedIcon from "@mui/icons-material/MedicationRounded";
import LaptopMacRoundedIcon from "@mui/icons-material/LaptopMacRounded";

const serviceCategories = [
  { id: "common", title: "Common Illnesses", icon: ThermostatRoundedIcon, services: ["Fever & Flu", "Allergies", "Infections", "Digestive Issues", "Respiratory Care"] },
  { id: "chronic", title: "Chronic Disease", icon: MonitorHeartRoundedIcon, services: ["Diabetes Management", "Hypertension", "Heart Health", "Thyroid Care", "Autoimmune Conditions"] },
  { id: "mental", title: "Mental Health", icon: PsychologyRoundedIcon, services: ["Therapy & Counseling", "Anxiety Treatment", "Depression Care", "Stress Management", "Behavioral Health"] },
  { id: "pain", title: "Pain Management", icon: AccessibilityNewRoundedIcon, services: ["Back Pain", "Joint Pain", "Physiotherapy", "Sports Injuries", "Arthritis Care"] },
  { id: "preventive", title: "Preventive Care", icon: ShieldRoundedIcon, services: ["Health Checkups", "Vaccinations", "Cancer Screening", "Wellness Programs", "Lifestyle Coaching"] },
  { id: "specialty", title: "Specialty Care", icon: MedicalServicesRoundedIcon, services: ["Dermatology", "ENT Services", "Ophthalmology", "Cardiology", "Neurology"] },
  { id: "women", title: "Women's Health", icon: GroupsRoundedIcon, services: ["Gynecology", "Fertility Care", "Prenatal Care", "Menopause Support", "Breast Health"] },
  { id: "pediatric", title: "Pediatric Care", icon: ChildCareRoundedIcon, services: ["Child Wellness", "Vaccinations", "Growth Monitoring", "Pediatric Specialists", "School Health"] },
  { id: "pharmacy", title: "Pharmacy Services", icon: MedicationRoundedIcon, services: ["Prescription Delivery", "Medication Management", "Discounted Labs", "Home Testing Kits", "Supplements"] },
  { id: "telehealth", title: "Telehealth", icon: LaptopMacRoundedIcon, services: ["Video Consultations", "Chat Support", "Remote Monitoring", "E-Prescriptions", "Follow-up Care"] }
];

export default function ServicesPreview() {
  const [activeCategory, setActiveCategory] = useState(serviceCategories[0].id);

  const activeServices = useMemo(
    () => serviceCategories.find((c) => c.id === activeCategory),
    [activeCategory]
  );

  return (
    <Box component="section" className="section-padding" sx={{ bgcolor: "background.default" }}>
      <Box className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={{ textAlign: "center", maxWidth: 768, margin: "0 auto 64px" }}
        >
          <Box
            component="span"
            sx={{
              display: "inline-block",
              px: 2,
              py: 0.75,
              borderRadius: 999,
              bgcolor: "#e4f3ee",
              color: "#1f7f72",
              fontSize: 14,
              fontWeight: 600,
              mb: 2
            }}
          >
            Our Specialties
          </Box>

          <Typography
            component="h2"
            sx={{
              fontSize: { xs: "1.875rem", md: "2.25rem", lg: "3rem" },
              fontWeight: 800,
              mb: 3,
              lineHeight: 1.15
            }}
          >
            Complete Healthcare
            <Box component="span" className="gradient-text" sx={{ display: "block" }}>
              Under One Roof
            </Box>
          </Typography>

          <Typography sx={{ fontSize: "1.125rem", color: "text.secondary" }}>
            From routine checkups to specialized treatments, we cover every aspect of your family's
            health needs.
          </Typography>
        </motion.div>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 2fr" }, gap: 4 }}>
          {/* Tabs */}
          <Box>
            <Box className="glass-card" sx={{ p: 2, position: { lg: "sticky" }, top: { lg: 128 } }}>
              <Box sx={{ display: "grid", gap: 1 }}>
                {serviceCategories.map((c) => {
                  const active = activeCategory === c.id;
                  const Icon = c.icon;
                  return (
                    <Box
                      key={c.id}
                      component="button"
                      onClick={() => setActiveCategory(c.id)}
                      sx={{
                        width: "100%",
                        border: "none",
                        textAlign: "left",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 1.5,
                        borderRadius: "16px",
                        bgcolor: active ? "primary.main" : "transparent",
                        color: active ? "#fff" : "text.primary",
                        boxShadow: active ? "var(--shadow-soft)" : "none",
                        transition: "all 300ms ease",
                        "&:hover": {
                          bgcolor: active ? "primary.main" : "hsl(var(--muted))"
                        }
                      }}
                    >
                      <Icon sx={{ fontSize: 20, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: 14, fontWeight: 600, flex: 1, color: "inherit" }}>
                        {c.title}
                      </Typography>
                      <ChevronRightRoundedIcon
                        sx={{
                          fontSize: 18,
                          transform: active ? "rotate(90deg)" : "rotate(0deg)",
                          transition: "transform 250ms ease"
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          {/* Details */}
          <Box>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeCategory}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Box className="premium-card" sx={{ p: 4 }}>
                  {activeServices && (
                    <>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                        <Box
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: "16px",
                            bgcolor: "rgba(39,165,153,0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}
                        >
                          <activeServices.icon sx={{ fontSize: 32, color: "primary.main" }} />
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: "1.5rem", fontWeight: 800 }}>
                            {activeServices.title}
                          </Typography>
                          <Typography sx={{ color: "text.secondary" }}>
                            Comprehensive care services
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                          gap: 2,
                          mb: 4
                        }}
                      >
                        {activeServices.services.map((s, idx) => (
                          <motion.div
                            key={s}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.05 }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                p: 2,
                                borderRadius: "16px",
                                bgcolor: "rgba(0,0,0,0.03)",
                                transition: "background-color 200ms ease",
                                "&:hover": { bgcolor: "hsl(var(--muted))" }
                              }}
                            >
                              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "primary.main" }} />
                              <Typography sx={{ fontWeight: 600 }}>{s}</Typography>
                            </Box>
                          </motion.div>
                        ))}
                      </Box>

                      <Button
                        component={Link}
                        to="/services"
                        variant="contained"
                        sx={{
                          bgcolor: "primary.main",
                          color: "#fff",
                          "&:hover": { bgcolor: "rgba(39,165,153,0.9)" }
                        }}
                        endIcon={<ArrowForwardRoundedIcon sx={{ transition: "transform 200ms" }} />}
                      >
                        View All Services
                      </Button>
                    </>
                  )}
                </Box>
              </motion.div>
            </AnimatePresence>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
